import { toast } from 'sonner';
import { useFormContext } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import PasswordIcon from '@mui/icons-material/Password';
import { Button, Stack, Typography } from '@mui/material';

import { CopyButton } from '../../../../../../../components/ui/CopyButton.jsx';
import type { EnableMHRAccessFormData } from './EnableMHRAccessModal.tsx';

const ViewAccessCode: React.FC<{ code: string | null }> = ({ code }) => {
  if (!code) {
    return <Typography variant="caption">Generate an access code to view it</Typography>;
  }

  return (
    <Stack gap={0.5} alignItems="center" direction="row">
      <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        Your generated access code:
        <CopyButton
          noHighlight
          textToCopy={code}
          sx={{ fontSize: 'inherit', fontFamily: 'monospace' }}
          iconProps={{ sx: { fontSize: '0.6rem' } }}
        >
          {code}
        </CopyButton>
      </Typography>
    </Stack>
  );
};

/**
 * Creates a random 8 digit code
 */
const generateMHRAccessCode = () => Math.floor(10000000 + Math.random() * 90000000).toString();

export const GenerateMHRAccessCodeButton: React.FC = () => {
  const [latestCode, setLatestCode] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const { setValue } = useFormContext<EnableMHRAccessFormData>();

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => setIsCopied(false), 2000);
    }
  }, [isCopied]);

  const handleOnClick = () => {
    const code = generateMHRAccessCode();

    navigator.clipboard.writeText(code);

    setLatestCode(code);
    setIsCopied(true);

    setValue('accessCode', code, { shouldValidate: true });

    toast.success('MHR Access code generated', {
      description: 'A random 8-digit code has been generated for you and copied to your clipboard',
    });
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      gap={1}
      alignItems="center"
      sx={{ borderColor: 'divider', borderWidth: 1, borderStyle: 'solid', p: 1, borderRadius: 1 }}
    >
      <Stack flex={1} alignItems="flex-start">
        <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
          <PasswordIcon fontSize="small" />
          <Typography variant="body1" fontWeight="bold">
            Generate Access Code
          </Typography>
        </Stack>
        <Typography variant="caption">
          A random 8-digit code will be generated for you and copied to your clipboard
        </Typography>
        <ViewAccessCode code={latestCode} />
      </Stack>
      <Button
        variant="text"
        color="primary"
        onClick={handleOnClick}
        disabled={isCopied}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        Generate
      </Button>
    </Stack>
  );
};
